import { Slider } from '../ui/slider';
import { Field, FieldDescription, FieldError, FieldLabel } from '../ui/field';

interface FieldSliderProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Slider>,
  'value' | 'onValueChange'
> {
  label: string;
  description?: string;
  errors?: Array<{ message?: string } | undefined>;
  required?: boolean;
  value: number;
  onValueChange: (value: number) => void;
  /** Renders a formatted value badge next to the label, e.g. (v) => `${v}%` */
  formatValue?: (value: number) => string;
  /** Tick label below the slider start, e.g. "50%" */
  minLabel?: string;
  /** Tick label below the slider end, e.g. "100%" */
  maxLabel?: string;
}

export function FieldSlider({
  label,
  description,
  errors,
  required = false,
  value,
  onValueChange,
  formatValue,
  minLabel,
  maxLabel,
  ...rest
}: FieldSliderProps) {
  return (
    <Field data-invalid={errors?.some(e => e?.message) ? 'true' : undefined}>
      <div className='flex items-center justify-between'>
        <FieldLabel className='gap-1'>
          {label} {required && <span className='text-destructive'>*</span>}
        </FieldLabel>
        {formatValue && <span className='text-sm font-semibold text-accent'>{formatValue(value)}</span>}
      </div>

      <Slider
        value={[value]}
        onValueChange={(arr) => onValueChange(arr[0])}
        aria-invalid={errors?.some(e => e?.message) ? 'true' : undefined}
        {...rest}
      />

      {(minLabel || maxLabel) && (
        <div className='flex justify-between text-[10px] text-muted-foreground'>
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}

      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldError errors={errors} />
    </Field>
  );
}
